import React from 'react';
import styled from 'styled-components';
import {
  BasePicker,
  DocumentCardActions,
  IBasePickerProps,
  IDropdownOption,
  IPickerItemProps,
  Label,
} from 'office-ui-fabric-react';
import _, { reject } from 'lodash';

const PickerWrapper = styled.div<{ error: boolean }>`
  position: relative;

  span[role='list'] {
    position: relative;
    width: 100%;
    padding-left: 8px;
    padding-right: 25px;
  }

  .ms-BasePicker-text {
    height: 32px;
    min-width: 168px;

    border-color: ${({ error }) => (error ? 'rgb(168, 0, 0)' : '')};
  }
`;

const SelectedItem = styled.div`
  z-index: 1;
  height: 100%;
  white-space: nowrap;
  overflow: hidden;
`;

const SuggestedItem = styled.div`
  padding: 0 12px;
  line-height: 30px;
`;

const CrossWrapper = styled.div`
  position: absolute;
  width: 16px;
  height: 16px;
  top: -6.7px;
  right: 15px;
`;

interface IProps {
  fieldName: string;
  label?: string;
  options: IDropdownOption[];
  onChange: (name: string, value: any, isRemove?: boolean) => void;
  value: any;
  error?: string;
  touched?: boolean;
  maxOptions?: number;
  required?: boolean;
  inTable?: true;
  onBlur?: (field: string) => void;
  resetValue?: any;
  disabled?: boolean;
}

interface IState {
  shouldClearFocus: boolean
}

const MarkRequired = styled.span`
  color: rgb(168, 0, 0);
  padding-right: 12px;
`;

export default class ItemsPicker extends React.Component<IProps, IState> {
  state: IState = {
    shouldClearFocus: false
  }
  pickerRef: any = React.createRef<any>();
  componentRef: any = React.createRef();

  componentDidUpdate(prevProps: Readonly<IProps>): void {
    if (
      prevProps.options.length !== this.props.options.length &&
      this.props.value &&
      !this.props.options.find((option) => option.key === this.props.value)
    ) {
      this.handleRemoveItem();
    }
  }

  handleResolveSuggestions = (filter: string, suggestionsItems?: any[]) => {
    this.setState({ shouldClearFocus: true })
    if (this.props.value !== undefined && this.props.value !== null) {
      return [...this.props.options];
    }
    
    const options = this.props.options;
    return options
      .filter(
        (item: any) =>
          item.text
            .toString()
            .toLowerCase()
            .indexOf(filter.toString().toLowerCase()) !== -1
      )
      .filter((item: any) => !_.some(suggestionsItems, item))
      .slice(0, 50);
  };

  handleRemoveItem = () => {
    if (this.state.shouldClearFocus) {
      if (this.componentRef.current) {
        this.componentRef.current.input.current._inputElement.current.focus()
        this.componentRef.current.input.current._inputElement.current.blur()
      }
      if (this.pickerRef.current) {
        this.pickerRef.current._inputElement.current.focus()
        this.pickerRef.current._inputElement.current.blur()
      }
    }

    this.props.onChange(
      this.props.fieldName,
      this.props.resetValue !== undefined ? this.props.resetValue : undefined,
      true
    );
  };

  render() {
    const {
      label,
      onChange,
      fieldName,
      value,
      required,
      inTable,
      error,
      onBlur,
      disabled,
    } = this.props;

    const hasValue = value !== undefined && value !== null;

    return (
      <PickerWrapper error={!!error}>
        {label && (
          <Label>
            {label}
            {required && <MarkRequired> *</MarkRequired>}
          </Label>
        )}
        <BasePicker<IDropdownOption, IBasePickerProps<IDropdownOption>>
          ref={this.componentRef}
          disabled={disabled}
          onRenderSuggestionsItem={(props) =>
            this.renderSuggestionItem(props, hasValue)
          }
          onResolveSuggestions={this.handleResolveSuggestions}
          onItemSelected={(item?: IDropdownOption) => {
            onChange(fieldName, item!.key);
            this.pickerRef.current._value = '';
            return item!;
          }}
          pickerCalloutProps={{
            styles: {
              root: {
                display: hasValue ? 'none' : undefined,
              },
            },
          }}
          onRenderItem={this.renderItem}
          pickerSuggestionsProps={{
            suggestionsHeaderText: 'Предложенные варианты',
            noResultsFoundText: 'Не найдено',
          }}
          selectedItems={value ? [value] : []}
          onEmptyInputFocus={(items) => {

            return this.handleResolveSuggestions('', items)
            }
          
          }
          inputProps={
            {
              onClick: () => {
                if (hasValue) {
                  this.componentRef.current.dismissSuggestions();
                }
              },
              onBlur: () => {
                if (this.pickerRef.current) {
                  this.pickerRef.current._value = '';
                  if (onBlur) onBlur(fieldName);
                }
              },
              readOnly: !!value,
              ref: this.pickerRef,
              style: {
                position: 'absolute',
                top: inTable ? undefined : 30,
                left: 1,
                height: 30,
                width: 'calc(100% - 2px)',
              },
            } as any
          }
        />
        {error && (
          <span style={{ color: 'rgb(168,0,0)', fontSize: 12 }}>{error}</span>
        )}
      </PickerWrapper>
    );
  }

  renderItem = ({ item }: IPickerItemProps<any>) =>(
    <SelectedItem>
      <CrossWrapper>
        <DocumentCardActions
          actions={[
            {
              iconProps: { iconName: 'Cancel' },
              onClick: this.handleRemoveItem,
            },
          ]}
          styles={{
            root: {
              padding: '0 !important',
            },
          }}
        />
      </CrossWrapper>
      {(() => {
        const selected = this.props.options.find(
          (option) => option.key === item
        );
        return selected ? selected.text : ' ';
      })()}
    </SelectedItem>
  );

  renderSuggestionItem = (props: IDropdownOption, selected: boolean) => {
    let itemText = props.text;

    if (itemText.length > 75) {
      itemText = itemText.slice(0, 75) + '...';
    }

    return <SuggestedItem>{itemText}</SuggestedItem>;
  };
}
